import React, { useState } from 'react';
import { Navigate, useLocation, Outlet } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, Toolbar, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; // Added missing import
import routes from "../Config/routes";
import useAuth from "../Hooks/use-auth";
import { useCreatorAuth } from "../Hooks/creator-use-auth";
import useIsDesktop from '../Hooks/useIsDesktop';
import { lightTheme } from '../Utils/baseTheme';
import { drawerWidth } from '../Utils/constants';
import { ThemeProvider } from "@mui/material/styles";
// Import the navbars
import Navbar from '../Components/Navbar/NavBar'; // Regular user navbar
import CreatorNavbar from '../Components/CreatorNavbar'; // Creator navbar
import BlitzHeader from '../Components/BlitzHeader';
import CreatorHeader from '../Components/CreatorHeader';

// Layout mixins
const openedMixin = (theme) => ({
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${drawerWidth})`,
  },
  marginInlineStart: {
    xs: 0,
    md: drawerWidth,
  },
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create(['min-width', 'margin-inline-start'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  minWidth: {
    xs: '100%',
    md: `calc(100% - ${lightTheme.spacing(8)} + 1px)`,
  },
  marginInlineStart: {
    xs: 0,
    md: `calc(${lightTheme.spacing(8)} - 1px)`,
  },
});

export default function GlobalRoute({ onHelp }) {
  const { isAuthorized: isUserAuthenticated } = useAuth();
  const { isAuthorized: isCreatorAuthenticated, isInitialized } = useCreatorAuth();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(isDesktop);
  const [mobileActions, setMobileActions] = useState([]);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const isExtended = (!isDesktop || isOpen);

  // Wait for creator auth to initialize
  if (!isInitialized) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if either type of user is authenticated
  if (!isUserAuthenticated && !isCreatorAuthenticated) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("redirect", location.pathname + location.search);
    return <Navigate to={`${routes.login}?${searchParams.toString()}`} />;
  }

  const handleGetMobileActions = (actions) => setMobileActions(actions);
  const handleUnreadCountChange = (count) => setUnreadCount(count);

  // Render the appropriate navbar and header based on authentication
  const renderLayout = () => {
    if (isCreatorAuthenticated) {
      // Creator layout
      const MenuButton = !isDesktop ? (
        <IconButton
          edge="start"
          aria-label="menu"
          sx={{ mr: 2 }}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <MenuIcon sx={{ color: '#fff' }} />
        </IconButton>
      ) : null;

      return (
        <>
          <CreatorNavbar
            isDesktop={isDesktop}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onToggleExtend={() => setIsOpen(prev => !prev)}
            onUnreadCountChange={handleUnreadCountChange}
            onGetMobileActions={handleGetMobileActions}
          />
          
          {!isDesktop && (
            <>
              <CreatorHeader
                menuIcon={MenuButton}
                isDesktop={isDesktop}
                mobileActions={mobileActions}
              />
              <Toolbar />
            </>
          )}
        </>
      );
    } else if (isUserAuthenticated) {
      // Regular user layout
      return (
        <>
          <Navbar
            isDesktop={isDesktop}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onClickHelp={onHelp}
            onOpen={() => setIsOpen(true)}
            onGetMobileActions={handleGetMobileActions}
            notificationAnchor={notificationAnchor}
            onSetNotificationAnchor={setNotificationAnchor}
          />
          
          {!isDesktop && (
            <>
              <BlitzHeader
                menuButton={null}
                isDesktop={isDesktop}
                mobileActions={mobileActions}
              />
              <Toolbar />
            </>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline enableColorScheme />
        {renderLayout()}
      </ThemeProvider>
      
      <Box
        id='GlobalPageContainer'
        sx={(theme) => ({
          ...(isExtended ? openedMixin(theme) : closedMixin(theme)),
        })}
      >
        <Outlet />
      </Box>
    </>
  );
}